import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TooltipModule } from 'primeng/tooltip';
import {
  AssistantChatAction,
  AssistantChatAnswered,
  AssistantChatClosed,
  AssistantChatFeedback,
  AssistantChatRating,
  AssistantChatBlock,
  AssistantChatMessage,
  AssistantChatPrompt,
  AssistantChatRenderedBlock,
  AssistantChatSegment,
  AssistantChatStage,
  AssistantChatSuggestion,
  AssistantChatViewMessage,
} from '../../interfaces/assistant-chat.interface';
import { AssistantChatService, AssistantChatState } from '../../services/assistant-chat/assistant-chat.service';
import { AssistantChatRatingService } from '../../services/assistant-chat/assistant-chat.rating.service';


const MAX_INPUT_HEIGHT_PX = 112;
const EXAMPLE_ACCEPT_KEY = 'Tab';
const BOLD_DELIMITER = '**';
const CODE_DELIMITER = '`';
const MAX_FOLLOWUPS = 2;
const NAME_WITH_ID = /^(.*\S)\s+#([\w.-]+)$/;

@Component({
  selector: 'assistant-chat',
  templateUrl: './assistant-chat.component.html',
  styleUrls: ['./assistant-chat.component.less'],
  imports: [CommonModule, FormsModule, TooltipModule],
  providers: [AssistantChatService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssistantChatComponent implements OnInit, OnDestroy {
  @Input()
  public set visible(value: boolean) {
    if (value === this.visibleValue) {
      return;
    }

    this.visibleValue = value;
    value ? this.markOpened() : this.markClosed('closed');
  }

  public get visible(): boolean {
    return this.visibleValue;
  }

  @Input()
  public set disabled(value: boolean) {
    const wasAvailable = !this.disabledValue;
    this.disabledValue = value;
    if (value && wasAvailable) {
      this.conversation.abandonAnswer();
    }
  }

  public get disabled(): boolean {
    return this.disabledValue;
  }

  @Input() disabledReason: string = '';
  @Input() contextLabel: string = '';
  @Input() contextDates: string = '';
  @Input() suggestions: AssistantChatSuggestion[] = [];
  @Input() title: string = 'Burt Assistant';
  @Input() subtitle: string = 'Connected to your Burthub data';
  @Input() choicesPrompt: string = 'What would you like to do?';
  @Input() guideHeading: string = 'For the best results, tell the Assistant';
  @Input() guideQuestions: string[] = ['What brings you to the app today?', 'What would a good answer help you do?'];

  @Input() exampleAnswer: string =
    "I'm meeting this advertiser on Thursday to renew their contract. I want talking points that show the campaign was worth the spend.";

  @Input() exampleNote: string = 'This is just a guide, you can ask anything.';
  @Input() inputPlaceholder: string = 'Ask a follow-up…';
  @Input() disclaimer: string = 'Uses AI. Verify results.';
  @Input() emptyAnswerReply: string = 'The answer came back empty.';
  @Input() failedAnswerReply: string = 'The answer could not be written.';
  @Input() abandonedAnswerReply: string = 'The question was left unanswered because the page changed.';
  @Input() failureLabel: string = "Couldn't answer";
  @Input() unknownBlockNote: string = 'This part of the answer came in a shape the panel cannot show.';

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() promptSubmitted = new EventEmitter<AssistantChatPrompt>();
  @Output() actionSelected = new EventEmitter<AssistantChatAction>();
  @Output() restarted = new EventEmitter<void>();
  @Output() answered = new EventEmitter<AssistantChatAnswered>();
  @Output() feedback = new EventEmitter<AssistantChatFeedback>();
  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<AssistantChatClosed>();

  @ViewChild('thread') private thread: ElementRef<HTMLElement>;
  @ViewChild('freeTextInput') private freeTextInput: ElementRef<HTMLTextAreaElement>;

  public messages: AssistantChatViewMessage[] = [];
  public stage: AssistantChatStage = 'choices';
  public typing: boolean = false;
  public draft: string = '';
  private readonly rendered = new WeakMap<AssistantChatMessage, AssistantChatRenderedBlock[]>();

  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private disabledValue: boolean = false;
  private readonly conversation = inject(AssistantChatService);
  private readonly ratingService = inject(AssistantChatRatingService);
  private openedAt: number = 0;
  private visibleValue: boolean = false;

  public get threadId(): string {
    return this.conversation.state.threadId;
  }

  public get messageCount(): number {
    return this.conversation.state.messages.length;
  }

  public get choicesVisible(): boolean {
    return this.stage === 'choices' && !this.messages.length;
  }

  public get guideVisible(): boolean {
    return this.stage === 'guided' && !this.typing;
  }

  public get inputVisible(): boolean {
    return this.stage !== 'choices';
  }

  public get followups(): string[] {
    const asked = new Set(
      this.messages
        .filter(message => message.role === 'user')
        .map(message => (message.text || '').trim().toLowerCase()),
    );
    const seen = new Set<string>();
    const kept: string[] = [];
    for (const message of [...this.messages].reverse()) {
      if (message.role !== 'assistant') {
        continue;
      }
      for (const block of message.blocks || []) {
        if (block.type !== 'followups') {
          continue;
        }
        for (const question of block.items) {
          const key = question.trim().toLowerCase();
          if (!key || asked.has(key) || seen.has(key)) {
            continue;
          }
          seen.add(key);
          kept.push(question);
        }
      }
    }

    return kept.slice(0, MAX_FOLLOWUPS);
  }

  public get exampleAvailable(): boolean {
    return this.guideVisible && !!this.exampleAnswer && !this.draft && !this.disabled;
  }

  public get activePlaceholder(): string {
    if (this.disabled && this.disabledReason) {
      return this.disabledReason;
    }

    return this.guideVisible ? this.exampleAnswer : this.inputPlaceholder;
  }

  public get canSend(): boolean {
    return !!this.draft.trim() && !this.typing && !this.disabled;
  }

  public get cannotAsk(): boolean {
    return this.typing || this.disabled;
  }

  public get canRestart(): boolean {
    return this.stage !== 'choices' || !!this.messages.length;
  }

  public get restartTitle(): string {
    return this.typing ? 'Wait for the answer before starting over' : 'Start over';
  }

  public ngOnDestroy(): void {
    this.markClosed('left');
  }

  public ngOnInit(): void {
    this.conversation.state$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => this.readState(state));
    this.conversation.answered$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.answered.emit(event));
  }

  public rate(index: number, rating: AssistantChatRating): void {
    const requestId = this.messages[index]?.meta?.requestId;
    if (!requestId || !this.conversation.rate(index, rating)) {
      return;
    }

    this.ratingService.rate(this.threadId, requestId, rating)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: () => undefined });
    this.feedback.emit({ index, rating, requestId });
  }

  public onActionClick(action: AssistantChatAction): void {
    this.actionSelected.emit(action);
  }

  public onFollowupClick(question: string): void {
    this.ask({ text: question, origin: 'followup' });
  }

  @HostListener('document:keydown.escape', ['$event'])
  public onEscape(event: KeyboardEvent): void {
    if (this.visible && !event.defaultPrevented) {
      this.close();
    }
  }

  public close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  public restart(): void {
    const ending = this.endingFrom('restarted');
    if (!this.conversation.restart()) {
      return;
    }

    if (ending) {
      this.closed.emit(ending);
    }

    this.draft = '';
    this.markOpened();
    this.restarted.emit();
  }

  public onSuggestionClick(suggestion: AssistantChatSuggestion): void {
    this.ask({ suggestionId: suggestion.id, text: suggestion.label, origin: 'suggestion' });
  }

  public onOtherClick(): void {
    this.conversation.openFreeText();
    this.readyForInput();
  }

  public onSend(): void {
    if (!this.canSend) {
      return;
    }

    const text = this.draft.trim();
    this.draft = '';
    this.ask({ text, origin: 'typed' });
  }

  public onKeydown(event: KeyboardEvent): void {
    if (event.key === EXAMPLE_ACCEPT_KEY && this.exampleAvailable) {
      event.preventDefault();
      this.acceptExample();
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSend();
    }
  }

  public onInput(): void {
    this.autoGrow();
  }

  public acceptExample(): void {
    this.draft = this.exampleAnswer;
    this.readyForInput();
  }

  private scrollToLatest(): void {
    setTimeout(() => {
      const thread = this.thread?.nativeElement;
      if (thread) {
        thread.scrollTop = thread.scrollHeight;
      }
    }, 0);
  }

  private readyForInput(): void {
    setTimeout(() => {
      this.autoGrow();
      this.freeTextInput?.nativeElement.focus();
    }, 0);
  }

  private ask(prompt: AssistantChatPrompt): void {
    if (this.disabled || !this.conversation.ask(prompt, {
      empty: this.emptyAnswerReply,
      failed: this.failedAnswerReply,
      abandoned: this.abandonedAnswerReply,
    })) {
      return;
    }

    this.promptSubmitted.emit(prompt);
  }

  private readState(state: AssistantChatState): void {
    const grew = state.messages.length !== this.messages.length;
    const answered = this.typing && !state.typing;

    this.messages = state.messages.map(message => this.view(message));
    this.stage = state.stage;
    this.typing = state.typing;

    if (grew || answered) {
      this.scrollToLatest();
    }

    if (answered) {
      this.readyForInput();
    }

    this.changeDetectorRef.markForCheck();
  }

  private view(message: AssistantChatMessage): AssistantChatViewMessage {
    if (message.role !== 'assistant' || !message.blocks) {
      return { role: message.role, text: message.text, meta: message.meta };
    }

    let blocks = this.rendered.get(message);
    if (!blocks) {
      blocks = this.render(message.blocks);
      this.rendered.set(message, blocks);
    }

    return { role: 'assistant', blocks, meta: message.meta };
  }

  private render(blocks: AssistantChatBlock[]): AssistantChatRenderedBlock[] {
    return blocks.map(block => {
      switch (block.type) {
        case 'text':
          return { type: block.type, title: block.title, segments: this.segments(block.body) };
        case 'table':
          return { ...block, rows: block.rows.map(row => row.map(cell => this.segments(cell))) };
        case 'points':
          return {
            ...block,
            items: block.items.map(point => ({ title: point.title, segments: this.segments(point.body) })),
          };
        case 'highlights':
          return {
            ...block,
            items: block.items.map(item => ({
              label: this.segments(item.label),
              value: item.value,
              context: this.segments(item.context || ''),
            })),
          };
        default:
          return block;
      }
    });
  }

  private segments(text: string): AssistantChatSegment[] {
    const segments: AssistantChatSegment[] = [];
    let plain = '';
    let bold = false;
    let index = 0;
    const flush = (): void => {
      if (plain) {
        segments.push({ text: plain, bold, code: false });
        plain = '';
      }
    };
    while (index < text.length) {
      if (this.opens(text, index, CODE_DELIMITER)) {
        const close = text.indexOf(CODE_DELIMITER, index + CODE_DELIMITER.length);
        flush();
        segments.push(this.named(text.slice(index + CODE_DELIMITER.length, close).split(BOLD_DELIMITER).join('')));
        index = close + CODE_DELIMITER.length;
      } else if (this.opens(text, index, BOLD_DELIMITER) || (bold && text.startsWith(BOLD_DELIMITER, index))) {
        flush();
        bold = !bold;
        index += BOLD_DELIMITER.length;
      } else {
        plain += text[index];
        index += 1;
      }
    }
    flush();
    return segments.filter(segment => !!segment.text);
  }

  private opens(text: string, index: number, delimiter: string): boolean {
    return text.startsWith(delimiter, index) && text.indexOf(delimiter, index + delimiter.length) !== -1;
  }

  private named(text: string): AssistantChatSegment {
    const match = NAME_WITH_ID.exec(text);
    return match ? { text: match[1], bold: false, code: true, id: match[2] } : { text, bold: false, code: true };
  }

  private autoGrow(): void {
    const textarea = this.freeTextInput?.nativeElement;
    if (!textarea) {
      return;
    }

    const measuringPlaceholder = !textarea.value;
    if (measuringPlaceholder) {
      textarea.value = textarea.placeholder;
    }

    textarea.style.height = 'auto';
    const contentHeight = textarea.scrollHeight;
    textarea.style.height = `${Math.min(contentHeight, MAX_INPUT_HEIGHT_PX)}px`;
    textarea.style.overflowY = contentHeight > MAX_INPUT_HEIGHT_PX ? 'auto' : 'hidden';

    if (measuringPlaceholder) {
      textarea.value = '';
    }
  }

  private markOpened(): void {
    this.openedAt = Date.now();
    this.opened.emit();
  }

  private markClosed(reason: string): void {
    const ending = this.endingFrom(reason);
    if (!ending) {
      return;
    }

    this.openedAt = 0;
    this.closed.emit(ending);
  }

  private endingFrom(reason: string): AssistantChatClosed | null {
    if (!this.openedAt) {
      return null;
    }

    const messages = this.conversation.state.messages;
    const answers = messages.filter(message => message.role === 'assistant');
    return {
      reason,
      durationMs: Date.now() - this.openedAt,
      messages: messages.length,
      answers: answers.length,
      failed: answers.filter(message => message.meta?.failed).length,
      ratedUp: answers.filter(message => message.meta?.rating === 'up').length,
      ratedDown: answers.filter(message => message.meta?.rating === 'down').length,
    };
  }
}
